import React, { useState, useContext } from 'react';
import { useMutation } from '@apollo/client';
import {
  Segment, Form, Button, Transition, Icon, Message,
} from 'semantic-ui-react';
import { AuthContext, PasswordInput } from '@dexter-experts/auth-react';
import PROFILE, { UPDATE_USER } from '../../queries/profile';

const PASSWORD_RULES = [
  {
    id: 'min-length',
    label: 'At least 8 characters',
    test: (value) => value.length >= 8,
  },
  {
    id: 'letter',
    label: 'At least 1 letter',
    test: (value) => /[A-Za-z]/.test(value),
  },
  {
    id: 'number',
    label: 'At least 1 number',
    test: (value) => /\d/.test(value),
  },
];

const Profile = ({ user }) => {
  const authContext = useContext(AuthContext) || {};
  const { authUser, changePassword } = authContext;
  const [firstName, setFirstName] = useState(user.firstName || '');
  const [lastName, setLastName] = useState(user.lastName || '');
  const [isSaved, setIsSaved] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [updateUser, { loading }] = useMutation(UPDATE_USER, {
    update(cache, { data }) {
      cache.writeQuery({
        query: PROFILE,
        data: { me: data.updateUser },
      });
    },
    onCompleted: () => {
      setIsSaved(true);
      setTimeout(() => setIsSaved(false), 3000);
    },
  });

  const onSave = () => {
    updateUser({
      variables: { input: { firstName, lastName } },
    });
  };

  const hasPassword = authUser?.hasPassword === true;
  const passwordTitle = hasPassword ? 'Change password' : 'Password Setup (Optional)';
  const passwordDescription = hasPassword
    ? 'Enter your current password and choose a new one.'
    : 'Create your first password to sign in without a magic link.';

  const savePassword = async () => {
    if (!changePassword) return;

    if (newPassword !== confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'Passwords do not match.' });
      return;
    }

    if (!PASSWORD_RULES.every((rule) => rule.test(newPassword))) {
      setPasswordMessage({
        type: 'error',
        text: 'Your password must be at least 8 characters and include a letter and a number.',
      });
      return;
    }

    setIsSavingPassword(true);
    setPasswordMessage(null);
    try {
      await changePassword({
        currentPassword: hasPassword ? currentPassword : undefined,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordMessage({
        type: 'success',
        text: 'Password updated.',
      });
    } catch (passwordError) {
      if (passwordError.message.includes('Invalid credentials')) {
        setPasswordMessage({
          type: 'error',
          text: 'Incorrect current password.',
        });
      } else {
        setPasswordMessage({
          type: 'error',
          text: "We couldn't update your password. Please try again.",
        });
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <div id="profile">
      <Segment>
        <Transition visible={isSaved} animation="scale" duration={500}>
          <Message success floating>
            <Message.Header>
              <Icon name="check" />
              Saved
            </Message.Header>
          </Message>
        </Transition>
        <Form>
          <Form.Group widths="equal">
            <Form.Input
              fluid
              label="First name"
              value={firstName}
              onChange={(e, { value }) => setFirstName(value)}
            />
            <Form.Input
              fluid
              label="Last name"
              value={lastName}
              onChange={(e, { value }) => setLastName(value)}
            />
          </Form.Group>
        </Form>
        <Button
          className="primary-btn"
          loading={loading}
          disabled={loading}
          onClick={onSave}
        >
          Save Updates
        </Button>
      </Segment>

      <Segment>
        {passwordMessage && (
          <Message
            positive={passwordMessage.type === 'success'}
            negative={passwordMessage.type === 'error'}
          >
            <Message.Content>{passwordMessage.text}</Message.Content>
          </Message>
        )}
        <h4>{passwordTitle}</h4>
        <p>{passwordDescription}</p>
        <Form>
          {hasPassword && (
            <PasswordInput
              required
              label="Current password"
              value={currentPassword}
              onChange={(event, { value }) => setCurrentPassword(value)}
            />
          )}
          <PasswordInput
            required
            label="New password"
            value={newPassword}
            onChange={(event, { value }) => setNewPassword(value)}
          />
          <div
            className="auth-password-rules"
            role="list"
            aria-label="Password requirements"
          >
            {PASSWORD_RULES.map((rule) => {
              const isMatched = rule.test(newPassword);
              return (
                <div
                  key={rule.id}
                  className={`auth-password-rule${isMatched ? ' is-met' : ''}`}
                  role="listitem"
                >
                  <input
                    type="checkbox"
                    checked={isMatched}
                    readOnly
                    tabIndex={-1}
                    aria-hidden="true"
                  />
                  <span>{rule.label}</span>
                </div>
              );
            })}
          </div>
          <PasswordInput
            required
            label="Confirm new password"
            value={confirmPassword}
            onChange={(event, { value }) => setConfirmPassword(value)}
          />
        </Form>
        <Button
          className="primary-btn"
          loading={isSavingPassword}
          disabled={
            isSavingPassword
            || !newPassword
            || !confirmPassword
            || (hasPassword && !currentPassword)
            || !changePassword
          }
          onClick={savePassword}
        >
          {hasPassword ? 'Change password' : 'Set up password'}
        </Button>
      </Segment>
    </div>
  );
};

export default Profile;
